import { useEffect, useMemo, useRef, useState, KeyboardEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Command } from 'cmdk';
import { Search, X } from 'lucide-react';
import { useThemeContext } from '@utils/hooks/use-theme-context';
import { useDebouncedValue } from '@utils/hooks/use-debounced-value';
import { useGlobalSearch } from '@utils/api/use-global-search';
import { useNavigationItems } from '@utils/hooks/use-navigation-items';
import {
  Bucket,
  BucketKey,
  assembleBuckets,
  bucketPriorityForRoute
} from './global-search-hits';
import BucketGroup from './global-search-bucket-group';
import { useGlobalSearchShortcuts } from './use-global-search-shortcuts';

const LIMIT_PER_CATEGORY = 5;

const DEBOUNCE_MS = 250;

function detectMac(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /Mac|iPhone|iPad|iPod/.test(navigator.platform);
}

export default function GlobalSearch() {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();
  const navigate = useNavigate();
  const location = useLocation();

  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  // Notify home.tsx to deactivate any global-search-triggered sort order
  // the moment the user starts typing a new search term.
  const handleQueryChange = (value: string) => {
    setQuery(value);
    if (value) {
      window.dispatchEvent(new CustomEvent('globalSearchTyping'));
    }
  };
  // Bucket order captured when the panel opens so it doesn't rearrange
  // while the user types. Reset on next open.
  const [priorityAtOpen, setPriorityAtOpen] = useState<BucketKey | null>(null);

  const inputRef = useRef<HTMLInputElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  // Skip the initial route effect so we don't clear the field on first mount.
  const initialPathRef = useRef(location.pathname);

  const debouncedQuery = useDebouncedValue(query, DEBOUNCE_MS);
  const trimmedDebounced = debouncedQuery.trim();
  const longEnough = trimmedDebounced.length >= 2;

  const { data, isFetching, isError } = useGlobalSearch({
    q: debouncedQuery,
    enabled: isOpen
  });

  // Freeze the priority bucket the moment the panel opens.
  useEffect(() => {
    if (isOpen) {
      setPriorityAtOpen(bucketPriorityForRoute(location.pathname));
    }
  }, [isOpen]);

  // Auto-clear on route change so the input doesn't feel "stuck" as the user
  // browses. Also closes the dropdown and drops the query. Skipped on first
  // mount so a query in the URL isn't wiped instantly.
  useEffect(() => {
    if (location.pathname === initialPathRef.current) return;
    initialPathRef.current = location.pathname;
    setQuery('');
    setIsOpen(false);
  }, [location.pathname]);

  // Close on outside click.
  useEffect(() => {
    if (!isOpen) return;
    const onMouseDown = (e: MouseEvent) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [isOpen]);

  useGlobalSearchShortcuts(inputRef, () => setIsOpen(true));

  const goTo = (href: string) => {
    setIsOpen(false);
    inputRef.current?.blur();
    navigate(href);
  };

  const onInputKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      if (query) {
        setQuery('');
      } else {
        setIsOpen(false);
        inputRef.current?.blur();
      }
    }
  };

  // Navigation bucket uses the same list the side-nav renders — new pages
  // added to `useNavigationItems` become searchable automatically.
  const { main: mainNav, reseller: resellerNav } = useNavigationItems();
  const navItems = useMemo(
    () => [...mainNav, ...resellerNav],
    [mainNav, resellerNav]
  );

  const orderedBuckets: Bucket[] = useMemo(
    () =>
      assembleBuckets({
        data,
        navItems,
        q: trimmedDebounced,
        limit: LIMIT_PER_CATEGORY,
        t,
        priority: priorityAtOpen
      }),
    [data, navItems, trimmedDebounced, t, priorityAtOpen]
  );

  const hasAnyResults = orderedBuckets.length > 0;
  const showEmpty = isOpen && longEnough && !isFetching && !hasAnyResults;

  const normalText = getColour('normalText');
  const inactiveText = getColour('inactiveText');
  const shortcutHint = detectMac() ? '⌘K' : 'Ctrl+K';

  return (
    <div ref={containerRef} className="w-full max-w-md relative">
      <Command shouldFilter={false} loop label={t('global-search') || 'Search'}>
        <div
          className="bg-app-soft border border-hairline flex items-center gap-2 h-9 px-2.5 rounded-2xl"
          style={{ color: normalText }}
        >
          <Search
            className="h-4 w-4 shrink-0"
            style={{ color: inactiveText }}
          />
          <Command.Input
            ref={inputRef}
            value={query}
            onValueChange={handleQueryChange}
            onFocus={() => setIsOpen(true)}
            onKeyDown={onInputKeyDown}
            placeholder={
              t('search-everything') || 'Search files, documents, folders…'
            }
            className="flex-1 bg-transparent outline-none text-sm placeholder:opacity-60"
            style={{ color: normalText }}
          />
          {query && (
            <button
              type="button"
              onClick={() => {
                setQuery('');
                inputRef.current?.focus();
              }}
              aria-label={t('clear') || 'Clear'}
              className="p-0.5 rounded hover:opacity-70"
              style={{ color: inactiveText }}
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
          {!query && (
            <kbd
              className="hidden md:inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-mono border border-hairline"
              style={{ color: inactiveText }}
              aria-hidden="true"
            >
              {shortcutHint}
            </kbd>
          )}
        </div>

        {isOpen && longEnough && (
          <Command.List
            className="bg-app-soft border border-hairline shadow-pop rounded-2xl mt-1 p-1 absolute left-0 right-0 z-50 overflow-y-auto"
            style={{
              color: normalText,
              top: 'calc(100% + 6px)',
              maxHeight: '60vh'
            }}
          >
            {isError && (
              <div
                className="px-3 py-3 text-sm"
                /* eslint-disable-next-line no-restricted-syntax -- semantic error color */
                style={{ color: '#EF4444' }}
              >
                {t('search-failed') || 'Search failed.'}
              </div>
            )}

            {showEmpty && (
              <Command.Empty
                className="px-3 py-3 text-sm"
                style={{ color: inactiveText }}
              >
                {t('no-results-found')}
              </Command.Empty>
            )}

            {!isError &&
              longEnough &&
              orderedBuckets.map(b => (
                <BucketGroup
                  key={b.key}
                  bucket={b}
                  inactiveText={inactiveText}
                  normalText={normalText}
                  onOpen={goTo}
                  onSeeAll={() => goTo(b.seeAllHref)}
                  t={t}
                />
              ))}

            {isFetching && (
              <div
                className="px-3 py-1.5 text-xs"
                style={{ color: inactiveText }}
              >
                {t('loading')}…
              </div>
            )}
          </Command.List>
        )}
      </Command>
    </div>
  );
}
